import React, { ChangeEvent, FormEvent, useState } from 'react';
import '../../App.css';
import { AuthService } from '../../APIService/AuthService';
import Typography from '@mui/material/Typography';
import { Box, Button } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Container from '@mui/material/Container';
import CircularProgress from '@mui/material/CircularProgress';
import { InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

const emailRegEx = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const passwordRegEx = /^(?=.*[0-9])(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&*]{6,16}$/;

type FormData = {
    email: string;
    securityCode: string;
    password: string;
    password2: string;
};

const ResetPassword: React.FC = () => {
    const [formData, setFormData] = useState<FormData>({
        email: '',
        securityCode: '',
        password: '',
        password2: '',
    });
    const [isPasswordError, setPasswordError] = useState(false);
    const [isPasswordFormatError, setPasswordFormatError] = useState(false);
    const [passwordErrorMessage, setPasswordErrorMessage] = useState('');
    const [isEmailError, setEmailError] = useState(false);
    const [emailErrorMessage, setEmailErrorMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [alertMesage, setAlertMesage] = useState('');
    const [alertHeader, setAlertHeader] = useState('');
    const [isSuccess, setIsSuccess] = useState(false);
    const [isHideForm, setIsHideForm] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault();

        if (!emailRegEx.test(formData.email)) {
            setEmailError(true);
            setEmailErrorMessage('Invalid email');
            return;
        }

        if (!passwordRegEx.test(formData.password)) {
            setEmailError(false);
            setPasswordFormatError(true);
            setPasswordErrorMessage('Password must contain one uppercase letter, one number, and one special character.');
            return;
        }

        if (formData.password !== formData.password2) {
            setPasswordFormatError(false);
            setEmailError(false);
            setPasswordError(true);
            setPasswordErrorMessage("Passwords do not match.");
            return;
        }

        setPasswordFormatError(false);
        setPasswordError(false);
        setEmailError(false);
        if (!loading) {
            setLoading(true);
        }

        try {
            const payload = {
                resetcode: formData.securityCode,
                newpassword: formData.password,
            };

            const response = await AuthService.resetPassword(formData.email, payload);

            if (response.success) {
                setOpen(true);
                setIsSuccess(true);
                setAlertHeader('Success');
                setAlertMesage('Password reset successful.');
            } else {
                const msg =
                    (response.errorResponse as { errorDescription?: string } | undefined)?.errorDescription ||
                    (typeof response.data === 'string' && response.data) ||
                    'Password reset failed, please try again';
                setOpen(true);
                setAlertHeader('Failed');
                setAlertMesage(msg);
            }
        } catch (error) {
            console.error(error);
            setOpen(true);
            setAlertHeader('Failed');
            setAlertMesage('Password reset failed, please try again');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
        const { name, value } = e.target;
        const field = name as keyof FormData;
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleSecurityCodeInput = (e: FormEvent<HTMLInputElement>): void => {
        const target = e.currentTarget;
        if (!target) return;
        const raw = typeof target.value === 'string' ? target.value : '';
        const sanitized = raw.replace(/\D/g, '').slice(0, 6);
        target.value = sanitized;
    };

    const handleClose = (): void => {
        if (isSuccess) {
            setIsHideForm(true);
        }
        setOpen(false);
    };

    return (
        <div>
            {!isHideForm && (
                <Container component="main" maxWidth="sm">
                    <CssBaseline />
                    <Box
                        sx={{
                            marginTop: 4,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            boxShadow: 3,
                            borderRadius: 2,
                            px: 4,
                            py: 6,
                        }}
                    >
                        <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
                            <AccountCircleIcon />
                        </Avatar>
                        <Typography component="h1" variant="h5">
                            Reset Password
                        </Typography>
                        <form onSubmit={handleSubmit}>
                            <Box sx={{ mt: 3 }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            name="email"
                                            required
                                            fullWidth
                                            id="email"
                                            label="Email"
                                            variant="standard"
                                            autoFocus
                                            error={isEmailError}
                                            helperText={isEmailError ? emailErrorMessage : ''}
                                            onChange={handleChange}
                                            value={formData.email}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            required
                                            fullWidth
                                            id="securityCode"
                                            label="Security Code"
                                            name="securityCode"
                                            type="number"
                                            onInput={handleSecurityCodeInput}
                                            variant="standard"
                                            onChange={handleChange}
                                            value={formData.securityCode}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            required
                                            fullWidth
                                            name="password"
                                            label="Password"
                                            type={showPassword ? 'text' : 'password'}
                                            id="password"
                                            error={isPasswordFormatError}
                                            helperText={isPasswordFormatError ? passwordErrorMessage : ''}
                                            variant="standard"
                                            onChange={handleChange}
                                            value={formData.password}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            onClick={(): void => setShowPassword((prev) => !prev)}
                                                            edge="end"
                                                            size="small"
                                                        >
                                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            required
                                            fullWidth
                                            error={isPasswordError}
                                            helperText={isPasswordError ? passwordErrorMessage : ''}
                                            name="password2"
                                            label="Confirm Password"
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            id="password2"
                                            variant="standard"
                                            onChange={handleChange}
                                            value={formData.password2}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            onClick={(): void => setShowConfirmPassword((prev) => !prev)}
                                                            edge="end"
                                                            size="small"
                                                        >
                                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                                <Box>
                                    <Button
                                        type="submit"
                                        value="submit"
                                        fullWidth
                                        disabled={loading}
                                        variant="contained"
                                        sx={{ mt: 5, mb: 1, height: 40 }}
                                    >
                                        {loading && (
                                            <CircularProgress
                                                size={24}
                                                sx={{
                                                    position: 'absolute',
                                                    top: '50%',
                                                    left: '50%',
                                                    marginTop: '-12px',
                                                    marginLeft: '-12px',
                                                }}
                                            />
                                        )}
                                        {loading ? '' : 'Submit'}
                                    </Button>
                                </Box>
                            </Box>
                        </form>
                    </Box>
                </Container>
            )}

            {isHideForm && (
                <Container component="main" maxWidth="sm">
                    <CssBaseline />
                    <Box
                        sx={{
                            marginTop: 4,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            boxShadow: 3,
                            borderRadius: 2,
                            px: 4,
                            py: 6,
                        }}
                    >
                        <h4>Password reset completed. Please try to login to smart filter controller mobile application.</h4>
                    </Box>
                </Container>
            )}
            <Dialog
                open={open}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">{alertHeader}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">{alertMesage}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Close</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default ResetPassword;
